import { useMemo } from 'react';
import { useGetAiImplementationAvailabilityQuery } from '../../ai-implementation/api/aiImplementationApi';
import type { Command } from '../../menu/types';
import { getCommandId } from './utils';
import { commandRegistry } from './registry';

type CommandAvailability = {
  isDisabled: (command: Command) => boolean;
};

export const useDetailCommandAvailability = (
  commands: Command[] | undefined,
  entityId: unknown
): CommandAvailability => {
  const hasAiImplementationAvailability = Boolean(
    commands?.some(
      (command) =>
        commandRegistry.getConfig(getCommandId(command))?.availability === 'aiImplementation'
    )
  );
  const numericEntityId = Number(entityId);
  const hasPersistedEntity = Number.isInteger(numericEntityId) && numericEntityId > 0;
  const aiAvailability = useGetAiImplementationAvailabilityQuery(numericEntityId, {
    skip: !hasAiImplementationAvailability || !hasPersistedEntity,
    refetchOnMountOrArgChange: true,
  });

  return useMemo(
    () => ({
      isDisabled: (command: Command) => {
        if (commandRegistry.getConfig(getCommandId(command))?.availability !== 'aiImplementation') {
          return false;
        }

        return (
          !hasPersistedEntity ||
          aiAvailability.isLoading ||
          aiAvailability.isError ||
          !aiAvailability.data?.canStart
        );
      },
    }),
    [
      aiAvailability.data?.canStart,
      aiAvailability.isError,
      aiAvailability.isLoading,
      hasPersistedEntity,
    ]
  );
};
